import React from 'react';
import {useIntl, IntlShape} from 'react-intl';
import {companyLogoSrc} from '../../../../directApi';
import {footerContactLine, formatQuoteDate} from '../quoteDocument';
import {parseTermsBlocks} from '../terms';
import PlateToHtml from './PlateToHtml';
import QuotePreviewEstimate from './QuotePreviewEstimate';
import type {QuoteDocumentData, QuoteTemplate} from '../resolver/types';
import * as S from './quotePreviewStyles';
import {sectionBody, sectionEnabled} from '../persistence/quoteTemplateSchema';
import {sectionTitle} from '../config/defaultTemplate';
import {themeTokens, type QuoteThemeTokens} from '../theme/themeTokens';

type PageProps = {
	data: QuoteDocumentData;
	template: QuoteTemplate;
};

type FramedPageProps = PageProps & {
	page: number;
	total: number;
	variant?: 'cover';
	children: React.ReactNode;
};

/** The A4 frame every preview page shares — the HTML twin of the PDF's QuotePage. */
const PreviewPage = ({data, template, page, total, variant, children}: FramedPageProps): JSX.Element => {
	const intl = useIntl();
	const logoSrc = template.branding.showLogo && data.companyLogoId ? companyLogoSrc(data.companyLogoId) : null;
	const runningHead = data.clientName ? `${data.clientName} · ${data.projectName}` : data.projectName;
	return (
		<S.PageSheet $font={template.branding.font} $cover={variant === 'cover'} data-cy="quote-preview-page">
			{variant !== 'cover' && template.layout.pageHeader && (
				<S.Band data-cy="quote-page-header">
					<S.BandRun>
						{logoSrc ? (
							<S.BandLogoImage src={logoSrc} crossOrigin="use-credentials" data-private alt="" />
						) : (
							<S.BandLogoWord>{data.companyName}</S.BandLogoWord>
						)}
						<S.BandRunText>{runningHead}</S.BandRunText>
					</S.BandRun>
					<S.BandConfidential>{intl.formatMessage({id: 'quote_designer.pdf.confidential'})}</S.BandConfidential>
				</S.Band>
			)}
			<S.PageContent>{children}</S.PageContent>
			{template.layout.pageFooter && (
				<S.Foot data-cy="quote-page-footer">
					<span>{footerContactLine(data.companyName, data.companyContactEmail)}</span>
					<span>{intl.formatMessage({id: 'quote_designer.pdf.page_of'}, {page, total})}</span>
				</S.Foot>
			)}
		</S.PageSheet>
	);
};

/** The theme a template resolves to. Pure, so it is recomputed rather than threaded as a prop. */
const tokensFor = (template: QuoteTemplate): QuoteThemeTokens =>
	themeTokens(template.layout.theme, template.branding.accentColor, template.branding.font);

const SectionHead = ({brand, tokens, children}: {brand: string; tokens: QuoteThemeTokens; children: string}): JSX.Element => (
	<S.SectionLabelRow $tokens={tokens}>
		<S.SectionLabelText $brand={brand} $tokens={tokens}>
			{children}
		</S.SectionLabelText>
	</S.SectionLabelRow>
);

/**
 * The cover: a full-bleed band carrying the logo, the kicker, the project title and who the quote
 * is for, over a two-column grid of the quote's facts. The HTML twin of pdf/QuoteCoverPage.
 */
const CoverBody = ({data, template}: PageProps): JSX.Element => {
	const intl = useIntl();
	const tokens = tokensFor(template);
	const logoSrc = template.branding.showLogo && data.companyLogoId ? companyLogoSrc(data.companyLogoId) : null;
	const showWordmark = template.branding.showLogo && !logoSrc;

	// Keyed by `cy`, not by the label: a hook built from the label changes with the reader's
	// language, so every selector against it would break in any locale but English.
	const metaCells: Array<{cy: string; label: string; value: string}> = [
		{
			cy: 'project',
			label: intl.formatMessage({id: 'quote_designer.cover.project'}),
			value: `${data.projectIndicator} \u00b7 ${data.projectName}`,
		},
		{cy: 'version', label: intl.formatMessage({id: 'quote_designer.cover.version'}), value: `v${data.revision}`},
		{cy: 'prepared-by', label: intl.formatMessage({id: 'quote_designer.cover.prepared_by'}), value: data.companyName},
		...(data.clientName
			? [
					{
						cy: 'prepared-for',
						label: intl.formatMessage({id: 'quote_designer.cover.prepared_for'}),
						value: data.clientName,
					},
			  ]
			: []),
		{
			cy: 'issued',
			label: intl.formatMessage({id: 'quote_designer.cover.quote_date'}),
			value: formatQuoteDate(data.issuedDate, intl),
		},
		{
			cy: 'valid-until',
			label: intl.formatMessage({id: 'quote_designer.cover.valid_until'}),
			value: formatQuoteDate(data.expiryDate, intl),
		},
	];

	return (
		<S.CoverContent>
			<S.CoverBand $tokens={tokens} data-cy="quote-cover-band">
				<div>
					{logoSrc ? <S.CoverLogoImage src={logoSrc} crossOrigin="use-credentials" data-private alt="" /> : null}
					{showWordmark ? <S.CoverLogoWord>{data.companyName}</S.CoverLogoWord> : null}
				</div>
				<S.CoverTitleBlock $tokens={tokens}>
					<S.CoverEyebrow $tokens={tokens}>{intl.formatMessage({id: 'quote_designer.pdf.eyebrow'})}</S.CoverEyebrow>
					<S.CoverTitle $tokens={tokens}>{data.projectName}</S.CoverTitle>
					{data.clientName ? (
						<S.CoverFor>
							{intl.formatMessage({id: 'quote_designer.pdf.prepared_for_client'}, {client: data.clientName})}
						</S.CoverFor>
					) : null}
				</S.CoverTitleBlock>
			</S.CoverBand>

			<S.CoverBody>
				<S.CoverMeta>
					{metaCells.map(cell => (
						<S.CoverMetaCell key={cell.cy} data-cy={`quote-cover-${cell.cy}`}>
							<S.CoverMetaLabel>{cell.label}</S.CoverMetaLabel>
							<S.CoverMetaValue>{cell.value}</S.CoverMetaValue>
						</S.CoverMetaCell>
					))}
				</S.CoverMeta>
			</S.CoverBody>
		</S.CoverContent>
	);
};

const NarrativeBody = ({template}: {template: QuoteTemplate}): JSX.Element => {
	const intl = useIntl();
	return (
		<>
			{sectionEnabled(template, 'INTRODUCTION') && (
				<S.Section data-cy="quote-preview-introduction">
					<SectionHead brand={template.branding.accentColor} tokens={tokensFor(template)}>
						{sectionTitle(template, 'INTRODUCTION', intl)}
					</SectionHead>
					<PlateToHtml value={sectionBody(template, 'INTRODUCTION')} />
				</S.Section>
			)}
			{sectionEnabled(template, 'SCOPE') && (
				<S.Section data-cy="quote-preview-scope">
					<SectionHead brand={template.branding.accentColor} tokens={tokensFor(template)}>
						{sectionTitle(template, 'SCOPE', intl)}
					</SectionHead>
					<PlateToHtml value={sectionBody(template, 'SCOPE')} />
				</S.Section>
			)}
		</>
	);
};

const TermsBody = ({template}: {template: QuoteTemplate}): JSX.Element => {
	const intl = useIntl();
	const terms = parseTermsBlocks(sectionBody(template, 'TERMS'));
	return (
		<S.Section data-cy="quote-preview-terms">
			<SectionHead brand={template.branding.accentColor} tokens={tokensFor(template)}>
				{sectionTitle(template, 'TERMS', intl)}
			</SectionHead>
			{terms.map((term, index) => (
				<S.TermRow key={index}>
					<S.TermNumber $brand={template.branding.accentColor}>{index + 1}</S.TermNumber>
					<S.TermBody>
						{term.heading ? <S.TermHeading>{term.heading}</S.TermHeading> : null}
						{term.body ? <S.TermText>{term.body}</S.TermText> : null}
					</S.TermBody>
				</S.TermRow>
			))}
		</S.Section>
	);
};

const SignBlock = ({party, first, intl}: {party: string; first: boolean; intl: IntlShape}): JSX.Element => (
	<S.SignParty $first={first}>
		<S.SignOrg>{party}</S.SignOrg>
		<div>
			<S.SignSignatureSpace />
			<S.SignLine />
			<S.SignLabel>{intl.formatMessage({id: 'quote_designer.pdf.signature'})}</S.SignLabel>
		</div>
		<S.SignField>
			<S.SignLine />
			<S.SignLabel>{intl.formatMessage({id: 'quote_designer.pdf.full_name'})}</S.SignLabel>
		</S.SignField>
		<S.SignField>
			<S.SignLine />
			<S.SignLabel>{intl.formatMessage({id: 'quote_designer.pdf.signer_title'})}</S.SignLabel>
		</S.SignField>
		<S.SignField>
			<S.SignLine />
			<S.SignLabel>{intl.formatMessage({id: 'common.date'})}</S.SignLabel>
		</S.SignField>
	</S.SignParty>
);

const AgreementBody = ({data, template}: PageProps): JSX.Element => {
	const intl = useIntl();
	return (
		<S.Section>
			<SectionHead brand={template.branding.accentColor} tokens={tokensFor(template)}>
				{intl.formatMessage({id: 'quote_designer.section.agreement'})}
			</SectionHead>
			<S.Paragraph>
				{intl.formatMessage(
					{id: 'quote_designer.pdf.agreement_body'},
					{reference: data.projectIndicator, expiry: formatQuoteDate(data.expiryDate, intl)}
				)}
			</S.Paragraph>
			<S.SignGrid>
				<SignBlock party={data.companyName} first intl={intl} />
				<SignBlock party={data.clientName || data.projectName} first={false} intl={intl} />
			</S.SignGrid>
			<S.SignNote>{intl.formatMessage({id: 'quote_designer.pdf.digital_signature_note'})}</S.SignNote>
		</S.Section>
	);
};

/**
 * The live document preview: the same pages, conditions, and computed content as the PDF the
 * designer downloads or publishes, rendered as plain HTML so every settings change repaints in
 * place — no PDF regeneration, no viewer reload, and the scroll position survives edits.
 */
const QuotePreview: React.FC<PageProps> = ({data, template}) => {
	const pages: Array<{key: string; variant?: 'cover'; body: React.ReactNode}> = [
		{key: 'cover', variant: 'cover', body: <CoverBody data={data} template={template} />},
	];
	if (sectionEnabled(template, 'INTRODUCTION') || sectionEnabled(template, 'SCOPE')) {
		pages.push({key: 'narrative', body: <NarrativeBody template={template} />});
	}
	pages.push({key: 'estimate', body: <QuotePreviewEstimate data={data} template={template} />});
	if (sectionEnabled(template, 'TERMS')) {
		pages.push({key: 'terms', body: <TermsBody template={template} />});
	}
	pages.push({key: 'agreement', body: <AgreementBody data={data} template={template} />});

	return (
		<div data-cy="quote-preview">
			{pages.map((page, index) => (
				<PreviewPage
					key={page.key}
					data={data}
					template={template}
					variant={page.variant}
					page={index + 1}
					total={pages.length}
				>
					{page.body}
				</PreviewPage>
			))}
		</div>
	);
};

// The document rebuilds from scratch on every prop change, so the pane's own view state - the
// page indicator and the zoom level - must not drag it through a re-render.
export default React.memo(QuotePreview);
